import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Image, Badge, Dropdown, List, Typography, Button, Empty, Skeleton } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import {
  SecondaryButton,
  DefaultButton,
} from '../Buttons/buttons';
import { 
  MailOutlined, 
  BellOutlined, 
  CheckOutlined 
} from '@ant-design/icons';
import { logout } from '../../auth/helpers';

import { useSelector, useDispatch  } from "react-redux";
import LogoutDropdown from './LogoutDropdown';
import LimitedAccessBadge from "./LimitedAccessBadge";
import {
  fetchUnreadNotificationCount,
  fetchUserNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from '../../services/notificationService';
import { WEBSITE_URL } from '../../Helper';
const { Text } = Typography;

function TopBar() {
  const [windowDimension, setWindowDimension] = useState(null);
  const userDetails = useSelector((state) => state?.User?.userDetails);
  const isLimitedAccess = userDetails?.limited_access;
  const [currentUser, setCurrentUser] = useState({});
  
  // Notification states
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    setCurrentUser(userDetails);
    setWindowDimension(window.innerWidth);
    function handleResize() {
      setWindowDimension(window.innerWidth);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails]);

  const handleFetchUnreadCount = useCallback(() => {
    dispatch(fetchUnreadNotificationCount())
      .then((res) => {
        setUnreadCount(res.count || 0);
      })
      .catch((err) => {
        console.error('Error fetching unread count:', err);
      });
  }, [dispatch]);

useEffect(() => {
  if (currentUser?.user_id) {
    handleFetchUnreadCount();

    const interval = setInterval(handleFetchUnreadCount, 300000); // every 5 min
    return () => clearInterval(interval);
  }
}, [currentUser, handleFetchUnreadCount]);

  const isMobile = windowDimension <= 991;
  let navigate = useNavigate();

  // Fetch notifications when dropdown opens
const handleFetchNotifications = () => {
  setNotificationLoading(true);
  dispatch(fetchUserNotifications(1, 10)) // page 1, limit 10
    .then((res) => {
      setNotifications(res.data || []);
    })
    .catch((err) => {
      console.error('Error fetching notifications:', err);
    })
    .finally(() => {
      setNotificationLoading(false);
    });
};

 const handleMarkAsRead = (notificationId) => {
  dispatch(markNotificationAsRead(notificationId))
    .then(() => {
      // Update local state
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId ? { ...notif, is_read: '1' } : notif
        )
      );
      handleFetchUnreadCount(); // Reuse the previously refactored function
    })
    .catch((err) => {
      console.error('Error marking notification as read:', err);
    });
};

const handleMarkAllAsRead = () => {
  dispatch(markAllNotificationsAsRead())
    .then(() => {
      setNotifications(prev =>
        prev.map(notif => ({ ...notif, is_read: '1' }))
      );
      setUnreadCount(0);
    })
    .catch((err) => {
      console.error('Error marking all notifications as read:', err);
    });
};


  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (notification.is_read === '0') {
      handleMarkAsRead(notification.id);
    }

    if (notification.action_url) {
      if (currentUser?.user_role === 'um_trusted-dealer') {
        const allowedDealerPaths = ['/customer-leads', '/requested-inspections', '/projects', '/notifications', '/all-installers', '/trusted-dealer-dashboard'];
        const isAllowed = allowedDealerPaths.some(path => notification.action_url.startsWith(path));
        navigate(isAllowed ? notification.action_url : '/customer-leads');
      } else if (currentUser?.user_role === 'um_flooring-consultant') {
        const allowedConsultantPaths = ['/consultant-profile', '/consultant-dashboard', '/projects', '/notifications'];
        const isAllowed = allowedConsultantPaths.some(path => notification.action_url.startsWith(path));
        navigate(isAllowed ? notification.action_url : '/consultant-profile');
      } else {
        navigate(notification.action_url);
      }
    }

    setDropdownVisible(false);
  };


  // Format time ago
  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  // Notification dropdown content
 const notificationDropdown = (
  <div 
    className="notification-dropdown" 
    style={{ 
      width: 380, 
      maxHeight: 450, 
      backgroundColor: '#ffffff',
      border: '1px solid #d9d9d9',
      borderRadius: '8px',
      boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
      overflow: 'hidden'
    }}
  >
    <div style={{ 
      padding: '16px', 
      borderBottom: '1px solid #f0f0f0', 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      backgroundColor: '#fafafa'
    }}>
      <Text strong style={{ fontSize: '16px', color: '#262626' }}>Notifications</Text>
      {unreadCount > 0 && (
        <Button 
          type="link" 
          size="small" 
          onClick={handleMarkAllAsRead}
          icon={<CheckOutlined />}
          style={{ padding: '4px 8px', height: 'auto' }}
        >
          Mark all read
        </Button>
      )}
    </div>
    
    <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
      {notificationLoading ? (
        // Show skeletons while loading
        <div style={{ padding: '16px' }}>
          {[1, 2, 3].map((_, i) => (
            <div key={i} style={{ marginBottom: '16px' }}>
              <Skeleton active title={false} paragraph={{ rows: 2 }} />
            </div>
          ))}
        </div>
      ) : notifications.length === 0 ? (
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
          <Empty 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No notifications"
            style={{ margin: 0 }}
          />
        </div>
      ) : (
        <List
          dataSource={notifications}
          split={false}
          renderItem={(item, index) => (
            <List.Item
              style={{
                padding: '16px',
                cursor: 'pointer',
                backgroundColor: item.is_read === '0' ? '#f6ffed' : '#ffffff',
                borderLeft: item.is_read === '0' ? '4px solid #52c41a' : '4px solid transparent',
                borderBottom: index < notifications.length - 1 ? '1px solid #f5f5f5' : 'none',
                margin: 0,
                transition: 'all 0.2s ease'
              }}
              onClick={() => handleNotificationClick(item)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = item.is_read === '0' ? '#f0f9e7' : '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = item.is_read === '0' ? '#f6ffed' : '#ffffff';
              }}
            >
              <List.Item.Meta
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                    <Text 
                      strong={item.is_read === '0'}
                      style={{ 
                        fontSize: '14px', 
                        color: '#262626',
                        lineHeight: '1.4',
                        maxWidth: '260px'
                      }}
                    >
                      {item.title}
                    </Text>
                    <Text 
                      type="secondary" 
                      style={{ 
                        fontSize: '11px', 
                        marginLeft: '8px',
                        whiteSpace: 'nowrap',
                        color: '#8c8c8c'
                      }}
                    >
                      {timeAgo(item.created_at)}
                    </Text>
                  </div>
                }
                description={
                  <Text 
                    type="secondary" 
                    ellipsis={{ rows: 2 }}
                    style={{ 
                      fontSize: '13px',
                      color: '#595959',
                      lineHeight: '1.4'
                    }}
                  >
                    <span dangerouslySetInnerHTML={{ __html: item.message }} />
                  </Text>
                }
              />
              {item.is_read === '0' && (
                <div 
                  style={{ 
                    width: '6px', 
                    height: '6px', 
                    backgroundColor: '#52c41a', 
                    borderRadius: '50%',
                    marginLeft: '8px',
                    flexShrink: 0
                  }} 
                />
              )}
            </List.Item>
          )}
        />
      )}
    </div>
    
    {notifications.length > 0 && !notificationLoading && (
      <div style={{ 
        padding: '12px 16px', 
        borderTop: '1px solid #f0f0f0', 
        textAlign: 'center',
        backgroundColor: '#fafafa'
      }}>
        <Button 
          type="link" 
          onClick={() => {
            navigate('/notifications');
            setDropdownVisible(false);
          }}
          style={{ padding: '4px 0', height: 'auto', color: '#1890ff' }}
        >
          View All Notifications
        </Button>
      </div>
    )}
  </div>
);
  // Extracted Notification Bell Component
const NotificationBell = () => (
  <div style={{ position: 'relative', display: 'inline-block' }}>
    <Dropdown
      overlay={notificationDropdown}
      trigger={['click']}
      placement="bottomRight"
      overlayClassName="notification-dropdown-overlay"
      visible={dropdownVisible}
      onVisibleChange={(visible) => {
        setDropdownVisible(visible);
        if (visible) {
          handleFetchNotifications();
        }
      }}
      overlayStyle={{
        position: 'fixed',
        zIndex: 9999,
        marginTop: '8px'
      }}
    >
      <div 
        style={{ 
          display: 'flex',
          alignItems: 'center', 
          marginRight: '12px',
          cursor: 'pointer',
          padding: '4px 6px',
          borderRadius: '6px',
          transition: 'all 0.2s ease',
          backgroundColor: dropdownVisible ? '#f0f0f0' : 'transparent',
          height: '100%' 
        }}
        onMouseEnter={(e) => {
          if (!dropdownVisible) {
            e.currentTarget.style.backgroundColor = '#f5f5f5';
          }
        }}
        onMouseLeave={(e) => {
          if (!dropdownVisible) {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
      >
        <Badge count={unreadCount} size="small" offset={[0, 0]}>
          <BellOutlined 
            style={{ 
              fontSize: '16px', 
              color: dropdownVisible ? '#1890ff' : '#666',
              transition: 'color 0.2s ease'
            }} 
          />
        </Badge>
      </div>
    </Dropdown>
  </div>
);
  const handleClick = () => {
    navigate('/inspection-reports');
  };

  
  
  const handleClickClaim = () => {
    navigate('/claim-analyst-panel');
  };

  const handleClickManuf = () => {
    navigate('/manufacturer-panel');
  };

  const handleClickInstaller = () => {
    navigate('/projects');
  };

  const handleIndustry = () => {
    navigate('/industry-standards');
  };

  return (
    <div className="container-xxl">
      <div className="topBar">
        <Row gutter={16}>
          <Col
            xs={24}
            sm={24}
            md={12}
            lg={12}
            xl={12}
            className="d-flex align-items-center"
          >
            <div className="topMailSection d-flex">
              <Link
                to={'mailto:flooringinspections@gmail.com'}
                className="mailLink d-flex align-items-center"
              >
                <MailOutlined className="me-2" /> flooringinspections@gmail.com
              </Link>
              <div className="social-menu-header  d-flex">
                <ul className=" d-flex  px-2">
                  <li><a href="https://twitter.com/floor_detective/" target="blank"><Image preview={false} src="https://fddatainsp.s3.us-east-2.amazonaws.com/wp-content/uploads/2023/06/28055457/icons8-twitter-18.png"
                  /></a></li>
                  <li><a href="https://www.instagram.com/floor_detective/" target="blank"><Image preview={false} src="https://fddatainsp.s3.us-east-2.amazonaws.com/wp-content/uploads/2023/06/28064837/icons8-instagram-20.png"
                  /></a></li>
                  <li><a href="https://www.linkedin.com/company/floor-detective/" target="blank"><Image preview={false} src="https://fddatainsp.s3.us-east-2.amazonaws.com/wp-content/uploads/2023/06/28055509/icons8-linkedin-18.png"
                  /></a></li>
                  <li><a href="https://www.facebook.com/floordetective1"><Image preview={false} src="https://fddatainsp.s3.us-east-2.amazonaws.com/wp-content/uploads/2023/06/28055445/icons8-facebook-18.png"
                  /></a></li>
                </ul>
              </div>
            </div>
          </Col>
          <Col
            xs={12}
            sm={12}
            md={12}
            lg={12}
            xl={12}
            className="d-flex justify-content-end"
          >
            {isMobile ? (
              ""
            ) : (
              <div className="topbarBtnSection">
  <div className="btnGroup">
    {/* Manufacturer Role */}
    {currentUser?.user_role === 'um_manufacturer' && (
      <>
      <NotificationBell />
        <SecondaryButton
          type="secondary"
          handleClick={() => handleClickManuf()}
          text="Claim"
          className={window.location.pathname === '/manufacturer-panel' ? 'active me-2' : 'me-2'}
        />
        <SecondaryButton
          type="secondary"
          handleClick={() => handleIndustry()}
          text="Industry Standards"
          className={window.location.pathname === '/industry-standards' ? 'active' : ''}
        />
        <DefaultButton
          type="default"
          handleClick={logout}
          text="Log Out"
        />
        
      </>
    )}

    {/* Claim Analyst Role */}
    {currentUser?.user_role === 'um_claim-analyst' && (
      <>
      <NotificationBell />
        <SecondaryButton
          type="secondary"
          handleClick={() => handleClickClaim()}
          text="Claim"
          className={window.location.pathname === '/claim-analyst-panel' ? 'active me-2' : 'me-2'}
        />
        <SecondaryButton
          type="secondary"
          handleClick={() => handleIndustry()}
          text="Industry Standards"
          className={window.location.pathname === '/industry-standards' ? 'active' : ''}
        />
        <DefaultButton
          type="default"
          handleClick={logout}
          text="Log Out"
        />
        
      </>
    )}

    {/* Dealer, FS Manager, FS Inspector Roles */}
    {(currentUser?.user_role === 'um_dealer' || 
      currentUser?.user_role === 'um_fs_manager' || 
      currentUser?.user_role === 'um_fs_inspector') && (
      <DefaultButton
        type="default"
        handleClick={logout}
        text="Log Out"
      />
    )}

    {/* Trusted Dealer Role */}
    {currentUser?.user_role === 'um_trusted-dealer' && (
      <div className="d-flex align-items-center">
        <SecondaryButton
          type="secondary"
          handleClick={() => navigate('/customer-leads')}
          text="Customer Leads"
          className={(window.location.pathname === '/customer-leads' || window.location.pathname === '/') ? 'active me-2' : 'me-2'}
        />
        <SecondaryButton
          type="secondary"
          handleClick={() => navigate('/trusted-dealer-dashboard')}
          text="Dashboard"
          className={window.location.pathname === '/trusted-dealer-dashboard' ? 'active me-2' : 'me-2'}
        />
        <NotificationBell />
        <LogoutDropdown userDetails={currentUser} />
      </div>
    )}

    {/* Inspector Role */}
    {currentUser?.user_role === 'um_inspector' && (
      <div className="d-flex align-items-center">
        <LimitedAccessBadge isLimitedAccess={isLimitedAccess} />
        <SecondaryButton
          type="secondary"
          handleClick={() => handleClick()}
          text="Inspection Report"
          className={window.location.pathname === '/inspection-reports' ? 'active me-2' : 'me-2'}
        />
        <SecondaryButton
          type="secondary"
          handleClick={() => handleIndustry()}
          text="Industry Standards"
          className={window.location.pathname === '/industry-standards' ? 'active me-2' : 'me-2'}
        />
        <NotificationBell />
        <LogoutDropdown userDetails={currentUser} />
      </div>
    )}

    {/* Installer Role */}
    {currentUser?.user_role === 'um_installer' && (
      <>
        <SecondaryButton
          type="secondary"
          handleClick={() => handleClickInstaller()}
          text="Photo Projects"
          className={window.location.pathname === '/projects' || window.location.pathname === '/installer-panel' ? 'active me-2' : 'me-2'}
        />
        <DefaultButton
          type="default"
          handleClick={logout}
          text="Log Out"
        />
      </>
    )}

    {/* Flooring Consultant Role */}
    {currentUser?.user_role === 'um_flooring-consultant' && (
      <div className="d-flex align-items-center">
        <SecondaryButton
          type="secondary"
          handleClick={() => navigate('/consultant-profile')}
          text="Dashboard"
          className={window.location.pathname.startsWith('/consultant-') ? 'active me-2' : 'me-2'}
        />
        {(currentUser?.is_active_paid || currentUser?.is_premium || ['27', '28'].includes(String(currentUser?.membership_id))) && (
          <SecondaryButton
            type="secondary"
            handleClick={() => navigate('/projects')}
            text="Photo Projects"
            className={window.location.pathname === '/projects' ? 'active me-2' : 'me-2'}
          />
        )}
        <NotificationBell />
        <LogoutDropdown userDetails={currentUser} />
      </div>
    )}

    {/* Cleaning & Restoration Role */}
    {currentUser?.user_role === 'um_cleaning-restoration' && (
      <div className="d-flex align-items-center">
        <SecondaryButton
          type="secondary"
          handleClick={() => navigate('/cleaning-dashboard')}
          text="Company Dashboard"
          className={window.location.pathname.startsWith('/cleaning-') ? 'active me-2' : 'me-2'}
        />
        {(currentUser?.is_active_paid || currentUser?.is_premium || ['29'].includes(String(currentUser?.membership_id))) && (
          <>
            <SecondaryButton
              type="secondary"
              handleClick={() => navigate('/projects')}
              text="Photo Projects"
              className={window.location.pathname === '/projects' ? 'active me-2' : 'me-2'}
            />
            <SecondaryButton
              type="secondary"
              handleClick={() => window.open(`${WEBSITE_URL}/defects/`, '_blank')}
              text="Claims & Conditions"
              className="me-2"
            />
          </>
        )}
        <NotificationBell />
        <LogoutDropdown userDetails={currentUser} />
      </div>
    )}
  </div>
</div>


            )}
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default TopBar;